import React, { useEffect, useState } from 'react';
import {
    Card,
    Typography,
    Button,
    Form,
    Input,
    InputNumber,
    DatePicker,
    ConfigProvider,
    Checkbox,
    Radio,
    Tooltip,
    Space,
    Rate,
} from 'antd';
import {
    CalendarOutlined,
    SaveOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import locale from 'antd/locale/lt_LT';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getProgramsEvaluation, createOrUpdateProgramsEvaluation } from '../../core/center-accreditation/centerAccreditationApi';
import { ProgramInitiator, ProgramPreparer, ProgramEvaluator } from '../../core/center-accreditation/types';

import './ScProgramsEvaluation.scss';

// Set Lithuanian locale for dayjs
dayjs.locale('lt');

const { Text } = Typography;
const { TextArea } = Input;

interface ProgramsEvaluationFormValues {
    is_program_evaluation_regulated?: boolean | null;
    program_evaluation_documents?: string;
    program_initiators?: ProgramInitiator[];
    program_count_total?: number | null;
    program_count_accredited?: number | null;
    program_count_equivalent?: number | null;
    program_preparers?: ProgramPreparer[];
    program_evaluators?: ProgramEvaluator[];
    other_evaluator_description?: string;
    effectiveness_research_methods?: string[];
    program_improvement_frequency?: string | null;
    program_improvement_reasons?: string[];
    other_improvement_reason_description?: string;
    program_advertising_channels?: string[];
    other_advertising_description?: string;
}

// 10.2 Program initiators
const programInitiatorOptions = [
    { code: 'institucija', title: '10.2.1 – pati institucija' },
    { code: 'ministerija', title: '10.2.2 – Švietimo ir mokslo ministerija' },
    { code: 'savivaldybe', title: '10.2.3 – savivaldybės administracijos švietimo padalinys' },
    { code: 'mokyklu_vadovai', title: '10.2.4 – mokyklų vadovai, jų pavaduotojai ugdymui, ugdymą organizuojančių skyrių vedėjai' },
    { code: 'mokytojai', title: '10.2.5 – mokytojai' },
    { code: 'kiti', title: '10.2.6 – kiti (kitų sričių specialistai, pvz. raštvedžiai, buhalteriai, kiti specialistai)' },
];

// 10.4 Program preparers
const programPreparerOptions = [
    { code: 'institucijos_darbuotojai', title: '10.4.1 – Institucijos darbuotojai' },
    { code: 'mokytojai', title: '10.4.2 – Mokytojai' },
    { code: 'mokyklu_vadovai', title: '10.4.3 – Mokyklų vadovai, jų pavaduotojai ugdymui, ugdymą organizuojančių skyrių vedėjai' },
    { code: 'mokslininkai', title: '10.4.4 – Mokslininkai/Dėstytojai' },
    { code: 'ministerijos_darbuotojai', title: '10.4.5 – Ministerijų, savivaldybių administracijų ir kitų valstybės institucijų darbuotojai' },
    { code: 'komandos', title: '10.4.6 – Komandos' },
    { code: 'kiti', title: '10.4.7 – Kiti (įrašykite) užsienio šalių lektoriai, kitų sričių specialistai' },
];

// 10.5 Program evaluators
const programEvaluatorOptions = [
    { code: 'institucijos_darbuotojai', title: '10.5.1 – Institucijos darbuotojai' },
    { code: 'mokytojai', title: '10.5.2 – Mokytojai' },
    { code: 'mokyklu_vadovai', title: '10.5.3 – Mokyklų vadovai, jų pavaduotojai ugdymui, ugdymą organizuojančių skyrių vedėjai' },
    { code: 'mokslininkai', title: '10.5.4 – Mokslininkai' },
    { code: 'savivaldybes', title: '10.5.5 – Savivaldybių administracijų švietimo padalinių darbuotojai' },
    { code: 'ekspertu_grupe', title: '10.5.6 – Ekspertų grupė' },
    { code: 'kita', title: '10.5.7 – Kita (įrašykite)' },
];

// 10.6 Effectiveness research methods
const effectivenessResearchMethods = [
    { code: 'klientu_skaicius', title: '10.6.1 – sprendžia pagal programoje dalyvavusių klientų skaičių' },
    { code: 'klientu_apklausos', title: '10.6.2 – atlieka klientų specialias apklausas apie konkrečios programos naudingumą' },
    { code: 'turinio_analize', title: '10.6.3 – analizuoja pateiktos programos turinį' },
    { code: 'netiria', title: '10.6.4 – efektyvumo specialiai netiria' },
];

// 10.7 Program improvement frequency
const programImprovementFrequencyOptions = [
    { code: 'reguliariai', title: '10.7.1 – reguliariai, tam skiriamas nuolatinis dėmesys' },
    { code: 'pagal_poreiki', title: '10.7.2 – tobulinamos tik tada, kai išaiškėja toks klientų poreikis' },
    { code: 'netobulinamos', title: '10.7.3 – tai nėra daroma, tik kuriamos naujos programos, o senų atsisakoma' },
];

// 10.8 Program improvement reasons
const programImprovementReasonsOptions = [
    { code: 'klientu_poreikiai', title: '10.8.1 – klientų poreikių tyrimas' },
    { code: 'veiklos_tobulinimas', title: '10.8.2 – siekis tobulinti savo veiklą' },
    { code: 'ministerijos_rekomendacijos', title: '10.8.3 – Švietimo, mokslo ir sporto ministerijos specialistų rekomendacijos' },
    { code: 'savivaldybes_rekomendacijos', title: '10.8.4 – savivaldybės administracijos švietimo padalinio rekomendacijos' },
    { code: 'kintanti_aplinka', title: '10.8.5 – nuolat kintanti aplinka' },
    { code: 'kita', title: '10.8.6 – kita (įrašykite)' },
];

// 10.9 Program advertising channels
const programAdvertisingChannelsOptions = [
    { code: 'skelbimai_savivaldybems', title: '10.9.1 – skelbimais savivaldybių švietimo padaliniams, mokykloms' },
    { code: 'spauda', title: '10.9.2 – skelbimais pedagoginėje ir kitoje spaudoje' },
    { code: 'svetaine', title: '10.9.3 – skelbia informaciją savo interneto svetainėje' },
    { code: 'reklamines_akcijos', title: '10.9.4 – specialios reklaminėmis akcijomis (lankstinukai, specialūs leidiniai)' },
    { code: 'kita', title: '10.9.5 – kita (įrašykite)' },
];

const ScProgramsEvaluation: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { programsEvaluation, isProgramsEvaluationFetching } = useSelector((state: CombinedStates) => state.centerAccreditation);
    const { singleSchool } = useSelector((state: CombinedStates) => state.school);

    const [form] = Form.useForm();
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());

    useEffect(() => {
        dispatch(getProgramsEvaluation({ date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null }));
    }, [dispatch, selectedDate]);

    useEffect(() => {
        if (programsEvaluation) {
            // Initialize program initiators
            const initiators = programsEvaluation.program_initiators || programInitiatorOptions.map(opt => ({
                code: opt.code,
                title: opt.title,
                count: 0,
            }));

            // Initialize program preparers
            const preparers = programsEvaluation.program_preparers || programPreparerOptions.map(opt => ({
                code: opt.code,
                title: opt.title,
                count: 0,
                national_level_count: 0,
            }));

            // Initialize program evaluators
            const evaluators = programsEvaluation.program_evaluators || programEvaluatorOptions.map(opt => ({
                code: opt.code,
                title: opt.title,
                rating: null,
            }));

            form.setFieldsValue({
                is_program_evaluation_regulated: programsEvaluation.is_program_evaluation_regulated ?? null,
                program_evaluation_documents: programsEvaluation.program_evaluation_documents || '',
                program_initiators: initiators,
                program_count_total: programsEvaluation.program_count_total || null,
                program_count_accredited: programsEvaluation.program_count_accredited || null,
                program_count_equivalent: programsEvaluation.program_count_equivalent || null,
                program_preparers: preparers,
                program_evaluators: evaluators,
                other_evaluator_description: programsEvaluation.other_evaluator_description || '',
                effectiveness_research_methods: programsEvaluation.effectiveness_research_methods || [],
                program_improvement_frequency: programsEvaluation.program_improvement_frequency || null,
                program_improvement_reasons: programsEvaluation.program_improvement_reasons || [],
                other_improvement_reason_description: programsEvaluation.other_improvement_reason_description || '',
                program_advertising_channels: programsEvaluation.program_advertising_channels || [],
                other_advertising_description: programsEvaluation.other_advertising_description || '',
            });
        } else {
            // Initialize with default values
            form.setFieldsValue({
                is_program_evaluation_regulated: null,
                program_evaluation_documents: '',
                program_initiators: programInitiatorOptions.map(opt => ({
                    code: opt.code,
                    title: opt.title,
                    count: 0,
                })),
                program_count_total: null,
                program_count_accredited: null,
                program_count_equivalent: null,
                program_preparers: programPreparerOptions.map(opt => ({
                    code: opt.code,
                    title: opt.title,
                    count: 0,
                    national_level_count: 0,
                })),
                program_evaluators: programEvaluatorOptions.map(opt => ({
                    code: opt.code,
                    title: opt.title,
                    rating: null,
                })),
                other_evaluator_description: '',
                effectiveness_research_methods: [],
                program_improvement_frequency: null,
                program_improvement_reasons: [],
                other_improvement_reason_description: '',
                program_advertising_channels: [],
                other_advertising_description: '',
            });
        }
    }, [programsEvaluation, form]);

    const handleSave = async (values: ProgramsEvaluationFormValues) => {
        const requestData = {
            date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null,
            is_program_evaluation_regulated: values.is_program_evaluation_regulated !== null && values.is_program_evaluation_regulated !== undefined ? values.is_program_evaluation_regulated : null,
            program_evaluation_documents: values.program_evaluation_documents || null,
            program_initiators: (values.program_initiators || []).filter((item: ProgramInitiator) => item.count > 0).length > 0
                ? (values.program_initiators || []).filter((item: ProgramInitiator) => item.count > 0)
                : null,
            program_count_total: values.program_count_total ?? null,
            program_count_accredited: values.program_count_accredited ?? null,
            program_count_equivalent: values.program_count_equivalent ?? null,
            program_preparers: (values.program_preparers || []).filter((item: ProgramPreparer) => item.count > 0).length > 0
                ? (values.program_preparers || []).filter((item: ProgramPreparer) => item.count > 0)
                : null,
            program_evaluators: (values.program_evaluators || []).filter((item: ProgramEvaluator) => item.rating !== null && item.rating !== undefined).length > 0
                ? (values.program_evaluators || []).filter((item: ProgramEvaluator) => item.rating !== null && item.rating !== undefined)
                : null,
            other_evaluator_description: values.other_evaluator_description || null,
            effectiveness_research_methods: values.effectiveness_research_methods && values.effectiveness_research_methods.length > 0
                ? values.effectiveness_research_methods
                : null,
            program_improvement_frequency: values.program_improvement_frequency ?? null,
            program_improvement_reasons: values.program_improvement_reasons && values.program_improvement_reasons.length > 0
                ? values.program_improvement_reasons
                : null,
            other_improvement_reason_description: values.other_improvement_reason_description || null,
            program_advertising_channels: values.program_advertising_channels && values.program_advertising_channels.length > 0
                ? values.program_advertising_channels
                : null,
            other_advertising_description: values.other_advertising_description || null,
        };

        await dispatch(createOrUpdateProgramsEvaluation(requestData));
    };

    const handleRefresh = (date?: dayjs.Dayjs) => {
        const dateToUse = date || selectedDate;
        dispatch(getProgramsEvaluation({ date: dateToUse ? dateToUse.format('YYYY-MM-DD') : null }));
    };

    const handleRefreshClick = () => {
        handleRefresh();
    };

    return (
        <ConfigProvider locale={locale}>
            <div className="school-programs-evaluation">
                <Card title="Kvalifikacijos tobulinimo programų vadyba DIREKTORĖ" className="programs-evaluation-form-card" loading={isProgramsEvaluationFetching || false}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            {programsEvaluation?.period_description ? (
                                <Text strong style={{ fontSize: '16px' }}>
                                    <CalendarOutlined style={{ marginRight: 8 }} />
                                    {programsEvaluation.period_description}
                                </Text>
                            ) : (
                                <Text strong style={{ fontSize: '16px' }}>
                                    Pasirinktam laikotarpiui duomenys nepateikti, norėdami pateikti duomenis užpildykite ir išsaugokite formą.
                                </Text>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <DatePicker
                                placeholder="Pasirinkite datą"
                                value={selectedDate}
                                onChange={(date) => {
                                    const newDate = date || dayjs();
                                    setSelectedDate(newDate);
                                    handleRefresh(newDate);
                                }}
                                format="YYYY-MM-DD"
                                style={{ width: 180 }}
                                allowClear={false}
                                disabledDate={(current) => current && current > dayjs().endOf('day')} />
                            <Tooltip title="Atnaujinti programų vertinimo duomenis">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefreshClick}
                                    loading={!!isProgramsEvaluationFetching}>
                                    Atnaujinti
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                        initialValues={{
                            is_program_evaluation_regulated: null,
                            program_evaluation_documents: '',
                            program_initiators: programInitiatorOptions.map(opt => ({
                                code: opt.code,
                                title: opt.title,
                                count: 0,
                            })),
                            program_count_total: null,
                            program_count_accredited: null,
                            program_count_equivalent: null,
                            program_preparers: programPreparerOptions.map(opt => ({
                                code: opt.code,
                                title: opt.title,
                                count: 0,
                                national_level_count: 0,
                            })),
                            program_evaluators: programEvaluatorOptions.map(opt => ({
                                code: opt.code,
                                title: opt.title,
                                rating: null,
                            })),
                            other_evaluator_description: '',
                            effectiveness_research_methods: [],
                            program_improvement_frequency: null,
                            program_improvement_reasons: [],
                            other_improvement_reason_description: '',
                            program_advertising_channels: [],
                            other_advertising_description: '',
                        }}>

                        {/* 10.1 Program evaluation regulation */}
                        <Card title="10.1 Ar programų vertinimas institucijoje reglamentuotas dokumentais?" style={{ marginBottom: 16 }}>
                            <Form.Item name="is_program_evaluation_regulated">
                                <Radio.Group>
                                    <Radio value>10.1.1 – taip (nurodykite dokumentus)</Radio>
                                    <Radio value={false}>10.1.2 – ne</Radio>
                                </Radio.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.is_program_evaluation_regulated !== currentValues.is_program_evaluation_regulated}>
                                {({ getFieldValue }) => {
                                    const isRegulated = getFieldValue('is_program_evaluation_regulated');
                                    return isRegulated === true ? (
                                        <Form.Item name="program_evaluation_documents" style={{ marginTop: 8 }}>
                                            <TextArea rows={4} placeholder="Įrašykite dokumentus" />
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>
                        </Card>

                        {/* 10.2 Program initiators */}
                        <Card title="10.2 Kas inicijuoja mokytojų kvalifikacijos tobulinimo renginių programų kūrimą?" style={{ marginBottom: 16 }}>
                            <Form.List name="program_initiators">
                                {(fields) => (
                                    <>
                                        {fields.map((field, index) => {
                                            const option = programInitiatorOptions[index];
                                            return (
                                                <div key={field.key} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <Form.Item name={[field.name, 'code']} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                    <Form.Item name={[field.name, 'title']} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                    <div style={{ minWidth: 400 }}>
                                                        <Text>{option?.title}</Text>
                                                    </div>
                                                    <Form.Item name={[field.name, 'count']} style={{ margin: 0 }}>
                                                        <InputNumber min={0} placeholder="Skaičius" style={{ width: 120 }} />
                                                    </Form.Item>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </Form.List>
                        </Card>

                        {/* 10.3 Program counts */}
                        <Card title="10.3 Kokiu skaičiumi mokytojų kvalifikacijos tobulinimo renginių (seminarų, kursų, paskaitų ir kitų renginių) programų disponuoja institucija? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Form.Item name="program_count_total" label="10.3.1 Iš viso">
                                    <InputNumber min={0} placeholder="Įrašykite skaičių" style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item name="program_count_accredited" label="10.3.2 Akredituotų">
                                    <InputNumber min={0} placeholder="Įrašykite skaičių" style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item name="program_count_equivalent" label="10.3.3 Prilygintų akredituotoms">
                                    <InputNumber min={0} placeholder="Įrašykite skaičių" style={{ width: '100%' }} />
                                </Form.Item>
                            </Space>
                        </Card>

                        {/* 10.4 Program preparers */}
                        <Card title="10.4 Kas rengė institucijos įgyvendinamų mokytojų kvalifikacijos tobulinimo renginių (seminarų, kursų, paskaitų, konferencijų ir kitų renginių) programas? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Form.List name="program_preparers">
                                {(fields) => (
                                    <>
                                        <div style={{ marginBottom: 8, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, fontWeight: 'bold' }}>
                                            <div>Kategorija</div>
                                            <div>Programos</div>
                                            <div>Nacionalinio lygmens programos</div>
                                        </div>
                                        {fields.map((field, index) => {
                                            const option = programPreparerOptions[index];
                                            return (
                                                <div key={field.key} style={{ marginBottom: 12, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, alignItems: 'center' }}>
                                                    <Form.Item name={[field.name, 'code']} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                    <Form.Item name={[field.name, 'title']} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                    <div>
                                                        <Text>{option?.title}</Text>
                                                    </div>
                                                    <Form.Item name={[field.name, 'count']} style={{ margin: 0 }}>
                                                        <InputNumber min={0} placeholder="Skaičius" style={{ width: '100%' }} />
                                                    </Form.Item>
                                                    <Form.Item name={[field.name, 'national_level_count']} style={{ margin: 0 }}>
                                                        <InputNumber min={0} placeholder="Skaičius" style={{ width: '100%' }} />
                                                    </Form.Item>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </Form.List>
                        </Card>

                        {/* 10.5 Program evaluators */}
                        <Card title="10.5 Kas ekspertuoja institucijos įgyvendinamas programas? Įvertinkite balais nuo 0 iki 4 (4 - dažniausiai)" style={{ marginBottom: 16 }}>
                            <Form.List name="program_evaluators">
                                {(fields) => (
                                    <>
                                        {fields.map((field, index) => {
                                            const option = programEvaluatorOptions[index];
                                            return (
                                                <div key={field.key} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <Form.Item name={[field.name, 'code']} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                    <Form.Item name={[field.name, 'title']} hidden>
                                                        <Input />
                                                    </Form.Item>
                                                    <div style={{ minWidth: 500 }}>
                                                        <Text>{option?.title}</Text>
                                                    </div>
                                                    <Form.Item name={[field.name, 'rating']} style={{ margin: 0 }}>
                                                        <Rate count={4} />
                                                    </Form.Item>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </Form.List>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => {
                                    const prevEvaluators = prevValues.program_evaluators || [];
                                    const currentEvaluators = currentValues.program_evaluators || [];
                                    const prevKitaRating = prevEvaluators.find((e: ProgramEvaluator) => e.code === 'kita')?.rating;
                                    const currentKitaRating = currentEvaluators.find((e: ProgramEvaluator) => e.code === 'kita')?.rating;
                                    return prevKitaRating !== currentKitaRating;
                                }}>
                                {({ getFieldValue }) => {
                                    const evaluators = getFieldValue('program_evaluators') || [];
                                    const kitaEvaluator = evaluators.find((e: ProgramEvaluator) => e.code === 'kita');
                                    return kitaEvaluator && kitaEvaluator.rating !== null && kitaEvaluator.rating !== undefined && kitaEvaluator.rating > 0 ? (
                                        <Form.Item name="other_evaluator_description" style={{ marginTop: 16 }}>
                                            <TextArea rows={2} placeholder="Įrašykite kas ekspertuoja programas" />
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>
                        </Card>

                        {/* 10.6 Effectiveness research methods */}
                        <Card title="10.6 Kaip institucija tiria realizuojamų programų efektyvumą?" style={{ marginBottom: 16 }}>
                            <Form.Item name="effectiveness_research_methods">
                                <Checkbox.Group>
                                    {effectivenessResearchMethods.map((option) => (
                                        <div key={option.code} style={{ marginBottom: 8 }}>
                                            <Checkbox value={option.code}>{option.title}</Checkbox>
                                        </div>
                                    ))}
                                </Checkbox.Group>
                            </Form.Item>
                        </Card>

                        {/* 10.7 Program improvement frequency */}
                        <Card title="10.7 Ar dažnai turimos programos tobulinamos?" style={{ marginBottom: 16 }}>
                            <Form.Item name="program_improvement_frequency">
                                <Radio.Group>
                                    {programImprovementFrequencyOptions.map((option) => (
                                        <Radio key={option.code} value={option.code}>
                                            {option.title}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Form.Item>
                        </Card>

                        {/* 10.8 Program improvement reasons */}
                        <Card title="10.8 Kas sąlygoja parengtų programų tobulinimą?" style={{ marginBottom: 16 }}>
                            <Form.Item name="program_improvement_reasons">
                                <Checkbox.Group>
                                    {programImprovementReasonsOptions.map((option) => (
                                        <div key={option.code} style={{ marginBottom: 8 }}>
                                            <Checkbox value={option.code}>{option.title}</Checkbox>
                                        </div>
                                    ))}
                                </Checkbox.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => {
                                    const prevReasons = prevValues.program_improvement_reasons || [];
                                    const currentReasons = currentValues.program_improvement_reasons || [];
                                    return prevReasons.includes('kita') !== currentReasons.includes('kita');
                                }}>
                                {({ getFieldValue }) => {
                                    const reasons = getFieldValue('program_improvement_reasons') || [];
                                    return reasons.includes('kita') ? (
                                        <Form.Item name="other_improvement_reason_description" style={{ marginTop: 8 }}>
                                            <TextArea rows={2} placeholder="Įrašykite kitas priežastis" />
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>
                        </Card>

                        {/* 10.9 Program advertising channels */}
                        <Card title="10.9 Kaip institucija reklamuoja savo siūlomas programas?" style={{ marginBottom: 16 }}>
                            <Form.Item name="program_advertising_channels">
                                <Checkbox.Group>
                                    {programAdvertisingChannelsOptions.map((option) => (
                                        <div key={option.code} style={{ marginBottom: 8 }}>
                                            <Checkbox value={option.code}>{option.title}</Checkbox>
                                        </div>
                                    ))}
                                </Checkbox.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => {
                                    const prevChannels = prevValues.program_advertising_channels || [];
                                    const currentChannels = currentValues.program_advertising_channels || [];
                                    return prevChannels.includes('kita') !== currentChannels.includes('kita');
                                }}>
                                {({ getFieldValue }) => {
                                    const channels = getFieldValue('program_advertising_channels') || [];
                                    return channels.includes('kita') ? (
                                        <Form.Item name="other_advertising_description" style={{ marginTop: 8 }}>
                                            <TextArea rows={2} placeholder="Įrašykite kitus reklamos kanalus" />
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>
                        </Card>

                        {/* Form Actions */}
                        <div style={{ textAlign: 'right', marginTop: 24 }}>
                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                Išsaugoti
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>
        </ConfigProvider>
    );
};

export default ScProgramsEvaluation;
